import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

export default function ClientDocumentsPage() {
  const { memberships } = useOrg();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDocs = useCallback(async () => {
    if (!memberships?.length) { setDocs([]); setLoading(false); return; }
    try {
      const results = await Promise.all(
        memberships.map(m =>
          api.getMyForms(m.org_id)
            .then(rows => (rows || []).map(r => ({ ...r, _orgName: m.organization?.name })))
            .catch(() => [])
        )
      );
      const signed = results.flat()
        .filter(f => f.status === 'signed')
        .sort((a, b) => new Date(b.signed_at || b.created_at) - new Date(a.signed_at || a.created_at));
      setDocs(signed);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [memberships]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[760px] mx-auto px-4 md:px-7 py-6">
        <h1 className="text-[22px] font-bold text-primary-c mb-1">Documents</h1>
        <p className="text-[12px] text-muted-c mb-6">Your signed onboarding and intake forms.</p>

        {loading ? (
          <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-20 bg-tertiary rounded-xl animate-pulse" />)}</div>
        ) : docs.length === 0 ? (
          <div className="text-center py-16 bg-tertiary rounded-xl border border-theme">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-muted-c mb-3 opacity-40">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <p className="text-[13px] text-muted-c">No signed documents yet</p>
            <p className="text-[11px] text-muted-c mt-1">Completed forms will appear here once you sign them.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map(d => (
              <div key={d.id} className="bg-elevated rounded-xl border border-theme p-4 flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-[12px] bg-success-soft flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-primary-c truncate">
                    {d.template?.name || 'Untitled document'}
                  </div>
                  <div className="text-[11px] text-muted-c mt-0.5 truncate">
                    {d._orgName && <span>{d._orgName}<span className="opacity-40 mx-1">&middot;</span></span>}
                    Signed {d.signed_at ? format(new Date(d.signed_at), 'MMM d, yyyy') : '\u2014'}
                  </div>
                </div>
                {d.document_url ? (
                  <a
                    href={d.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3.5 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
                  >
                    Download
                  </a>
                ) : (
                  <span className="text-[11px] text-muted-c">Preparing...</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
